import React from 'react';
import { Search } from 'lucide-react';
import { SETTINGS_INPUT_CLASS } from '../utils/settingsSectionStyles';
import { IntegrationMark, IntegrationStateChip } from './integrations/integrationUi';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import {
  CATEGORY_ORDER,
  CTA,
  INTEGRATIONS,
  datevStatus,
  dkvStatus,
  getJson,
  idsStatus,
  leadApiStatus,
  meinshkStatus,
  metalStatus,
  omdStatus,
  type Integration,
  type Status,
} from './integrations/integrationCatalog';

type Statuses = Partial<Record<Integration['id'], Status>>;

// Letzter Stand je Instanz: beim erneuten Öffnen sofort zeigen und still aktualisieren.
const statusCache = new Map<string, { statuses: Statuses; showDatev: boolean }>();
const cacheKey = () => window.location.pathname.match(/^\/i\/[^/]+/)?.[0] ?? '';

export function IntegrationsSettingsSection({ onOpen }: { onOpen: (path: string) => void }) {
  const cached = statusCache.get(cacheKey());
  const [statuses, setStatuses] = React.useState<Statuses | null>(cached?.statuses ?? null);
  const [showDatev, setShowDatev] = React.useState(cached?.showDatev ?? false);
  const [query, setQuery] = React.useState('');
  // Integrations-IDs sind zugleich Modul-Keys: abgewählte Integrationen fehlen im Katalog.
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  // Status nur für eingeschaltete Integrationen laden (der Server sperrt die anderen). Als String,
  // damit ein erneutes /api/me ohne Änderung die Loader nicht neu startet.
  const enabledIntegrationIds = modulesLoaded
    ? INTEGRATIONS.map((item) => item.id).filter((id) => isEnabled(id)).join(',')
    : null;

  // Alles auf einmal setzen, damit Kacheln nicht zwischen „Verbunden“ und „Weitere“ springen.
  React.useEffect(() => {
    if (enabledIntegrationIds === null) return;
    const enabled = new Set(enabledIntegrationIds.split(','));
    let cancelled = false;
    let canManageDatev = false;
    // /api/datev/status antwortet immer; die Details (/api/settings/datev) nur mit DATEV-Modul.
    const loadDatev = async (): Promise<Status | null> => {
      const d = await getJson<{ enabled: boolean; configured: boolean; canManage: boolean }>('/api/datev/status');
      canManageDatev = d.canManage;
      if (!d.canManage || !enabled.has('datev')) return null;
      return datevStatus(d.enabled, d.configured).catch(() => ({ state: d.configured ? 'connected' : 'incomplete' }) as Status);
    };
    const loaders = ([
      ['datev', loadDatev],
      ['omd', omdStatus],
      ['ids', idsStatus],
      ['metal', metalStatus],
      ['dkv', dkvStatus],
      ['meinshk', meinshkStatus],
      ['lead_api', leadApiStatus],
    ] as Array<[Integration['id'], () => Promise<Status | null>]>).filter(([id]) => id === 'datev' || enabled.has(id));
    void Promise.allSettled(loaders.map(([, load]) => load())).then((results) => {
      if (cancelled) return;
      const next: Partial<Record<Integration['id'], Status>> = {};
      // Ohne Status bleibt die Kachel neutral; die Detailseite zeigt den Fehler.
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) next[loaders[index][0]] = result.value;
      });
      statusCache.set(cacheKey(), { statuses: next, showDatev: canManageDatev });
      setShowDatev(canManageDatev);
      setStatuses(next);
    });
    return () => {
      cancelled = true;
    };
  }, [enabledIntegrationIds]);


  const q = query.trim().toLowerCase();
  const visible = INTEGRATIONS.filter((item) => isEnabled(item.id)).filter((item) => item.id !== 'datev' || showDatev).filter(
    (item) => !q || `${item.name} ${item.description} ${item.category}`.toLowerCase().includes(q),
  );
  const isActive = (item: Integration) => {
    const state = statuses?.[item.id]?.state;
    return state != null && state !== 'off';
  };
  const connected = visible.filter(isActive);
  const others = visible.filter((item) => !isActive(item));

  return (
    <div className="-mx-3 -mt-3 px-3 pt-3 md:mx-0 md:mt-0 md:px-0 md:pt-0">
      <div>
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4 md:mb-8">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-app-text">Integrationen</h1>
            <p className="mt-1.5 text-sm text-app-text-muted">
              Verbinde Meisterpfad mit Steuerkanzlei, Großhandel und deinen eigenen Systemen.
            </p>
          </div>
          <label className="relative block w-full sm:w-64">
            <span className="sr-only">Integration suchen</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-text-subtle" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Integration suchen"
              className={`${SETTINGS_INPUT_CLASS} pl-9`}
            />
          </label>
        </div>

        {!statuses ? <SkeletonGrid /> : null}

        {statuses && visible.length === 0 ? (
          <div className="rounded-xl border border-dashed border-app-border-strong bg-app-surface p-10 text-center text-sm text-app-text-muted">
            Keine Integration gefunden.
          </div>
        ) : null}

        {statuses && connected.length > 0 ? (
          <CatalogSection title="Verbunden" count={connected.length}>
            <TileGrid items={connected} statuses={statuses} onOpen={onOpen} />
          </CatalogSection>
        ) : null}

        {statuses && others.length > 0 ? (
          <CatalogSection title={connected.length > 0 ? 'Weitere Integrationen' : 'Alle Integrationen'} count={others.length}>
            <TileGrid
              items={CATEGORY_ORDER.flatMap((category) => others.filter((item) => item.category === category))}
              statuses={statuses}
              onOpen={onOpen}
            />
          </CatalogSection>
        ) : null}
      </div>
    </div>
  );
}

function CatalogSection({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <section className="mb-8 md:mb-9">
      <div className="mb-3.5 flex items-baseline gap-2">
        <h2 className="text-base font-semibold text-app-text">{title}</h2>
        <span className="text-sm text-app-text-subtle">{count}</span>
      </div>
      {children}
    </section>
  );
}

function TileGrid({
  items,
  statuses,
  onOpen,
}: {
  items: Integration[];
  statuses: Partial<Record<Integration['id'], Status>>;
  onOpen: (path: string) => void;
}) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3 md:gap-4">
      {items.map((item) => {
        const status = statuses[item.id];
        const state = status?.state ?? 'off';
        const detailClass =
          status?.detailTone === 'err'
            ? 'font-medium text-red-700'
            : status?.detailTone === 'warn'
              ? 'font-medium text-amber-800'
              : 'text-app-text-muted';
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onOpen(item.path)}
            className={`group flex min-h-[176px] flex-col gap-3.5 rounded-xl border bg-app-surface p-5 text-left transition hover:-translate-y-px hover:border-blue-200 hover:shadow-[0_8px_24px_rgba(15,23,42,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30 ${
              state === 'error' ? 'border-red-200' : 'border-app-border'
            }`}
          >
            <div className="flex w-full items-center gap-3">
              <IntegrationMark mark={item.mark} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-semibold text-app-text">{item.name}</p>
                <p className="mt-0.5 text-xs text-app-text-muted">{item.category}</p>
              </div>
            </div>
            <p className="flex-1 text-sm leading-relaxed text-app-text-muted">{item.description}</p>
            <div className="flex w-full items-center justify-between gap-3 text-xs">
              <span className="flex min-w-0 items-center gap-1.5">
                {status ? <IntegrationStateChip state={state} /> : null}
                {status?.detail ? <span className={`min-w-0 truncate ${detailClass}`}>· {status.detail}</span> : null}
              </span>
              <span className="shrink-0 font-semibold text-pipedrive-blue">{CTA[state]} →</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/** Erster Besuch: neutrale Platzhalter statt echter Kacheln, damit nichts umsortiert wird. */
function SkeletonGrid() {
  return (
    <div aria-busy aria-label="Integrationen werden geladen" className="mb-8">
      <div className="mb-3.5 h-5 w-40 animate-pulse rounded bg-app-surface-muted" />
      <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3 md:gap-4">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="flex min-h-[176px] flex-col gap-3.5 rounded-xl border border-app-border bg-app-surface p-5">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 animate-pulse rounded-xl bg-app-surface-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-28 animate-pulse rounded bg-app-surface-muted" />
                <div className="h-3 w-20 animate-pulse rounded bg-app-surface-muted" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-3 w-full animate-pulse rounded bg-app-surface-muted" />
              <div className="h-3 w-4/5 animate-pulse rounded bg-app-surface-muted" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
